import i18nFactory from '@yandex-int/i18n';
import { Epic } from 'redux-observable';
import { combineLatest, Observable } from 'rxjs';

import { Action } from '../../../actions';
import {
    CANCEL_FREELANCER_SERVICE_BY_CLIENT_START,
    CancelFreelancerServiceByClientStartAction,
    cancelFreelancerServiceByClientSuccess,
    clearFreelancerFeedbackForm,
    ClearFreelancerFeedbackFormAction
} from '../../../actions/freelancers/feedback-form';
import {
    AddFreelancerFeedbackStartAction,
    addFreelancerFeedbackSuccess,
    AddFreelancerFeedbackSuccessAction,
    UpdateFreelancerFeedbackStartAction,
    updateFreelancerFeedbackSuccess,
    UpdateFreelancerFeedbackSuccessAction
} from '../../../actions/freelancers/freelancer-feedback';
import { mapFreelancerFeedbackItem } from '../../../mappers/freelancers';
import { freelancerFeedbackErrorHandler } from '../../../service/FreelancerFeedbackErrorHandler/FreelancerFeedbackErrorHandler';
import { showNoticeMessage } from '../../../service/GridHelpers/GridHelpers';
import { EpicDeps } from '../../../service/StoreFactory';
import { NoticeType } from '../../../typings/grid';
import { State } from '../../../typings/state';

import * as keyset from './feedback-form.i18n';

const i18n = i18nFactory(keyset);

export const cancelCooperationByClientEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    const toCancelFreelancerService$ = action$
        .ofType(CANCEL_FREELANCER_SERVICE_BY_CLIENT_START)
        .mergeMap((action: CancelFreelancerServiceByClientStartAction) => {
            return Observable.fromPromise(
                deps.freelancerMutationExecutor.cancelFlServiceByClient({
                    freelancerId: action.payload.freelancerId,
                    projectId: action.payload.projectId
                })
            )
                .map(response => response.cancelFlServiceByClient)
                .map(cancelServiceData => cancelFreelancerServiceByClientSuccess(cancelServiceData));
        });

    const toAddFeedback$ = action$
        .ofType(CANCEL_FREELANCER_SERVICE_BY_CLIENT_START)
        .filter((action: CancelFreelancerServiceByClientStartAction) => action.payload.feedbackId === undefined)
        .mergeMap((action: AddFreelancerFeedbackStartAction) => {
            return Observable.fromPromise(
                deps.freelancerMutationExecutor.addFeedbackForFreelancer({
                    willRecommend: action.payload.willRecommend,
                    freelancerId: action.payload.freelancerId,
                    feedbackText: action.payload.feedbackText,
                    overallMark: String(action.payload.freelancerScore)
                })
            )
                .map(response => {
                    const { feedback } = response.addFeedbackForFreelancer;
                    return {
                        freelancerId: String(feedback.freelancerId),
                        item: mapFreelancerFeedbackItem(feedback)
                    };
                })
                .map(({ freelancerId, item }) => addFreelancerFeedbackSuccess(freelancerId, item))
                .catch(error => {
                    freelancerFeedbackErrorHandler(error.errors);

                    throw error;
                });
        });

    const toUpdateFeedback$ = action$
        .ofType(CANCEL_FREELANCER_SERVICE_BY_CLIENT_START)
        .filter((action: CancelFreelancerServiceByClientStartAction) => action.payload.feedbackId !== undefined)
        .mergeMap((action: UpdateFreelancerFeedbackStartAction) => {
            return Observable.fromPromise(
                deps.freelancerMutationExecutor.updateFreelancerFeedback({
                    feedbackId: action.payload.feedbackId as string,
                    overallMark: String(action.payload.freelancerScore),
                    willRecommend: action.payload.willRecommend,
                    freelancerId: action.payload.freelancerId,
                    feedbackText: action.payload.feedbackText
                })
            )
                .map(response => response.updateFreelancerFeedback.feedback)
                .map(feedback => updateFreelancerFeedbackSuccess(mapFreelancerFeedbackItem(feedback)));
        });

    return combineLatest(toCancelFreelancerService$, toAddFeedback$.merge(toUpdateFeedback$))
        .mergeMap(([cancelServiceAction, feedbackAction]) => {
            showNoticeMessage(NoticeType.success, i18n('success-message'));
            return Observable.concat(
                Observable.of(cancelServiceAction),
                Observable.of(feedbackAction),
                Observable.of(clearFreelancerFeedbackForm())
            );
        })
        .catch(() => {
            showNoticeMessage(NoticeType.error, i18n('error-message'));
            return Observable.of(clearFreelancerFeedbackForm());
        }) as Observable<
        | AddFreelancerFeedbackSuccessAction
        | UpdateFreelancerFeedbackSuccessAction
        | ClearFreelancerFeedbackFormAction
        | CancelFreelancerServiceByClientStartAction
    >;
};
